// src/components/roads/AIClusteredWorkOrdersTable.js
// --- UPDATE (v2.0 AI-connected)
//   • Shows everything the AI dispatch produced: road / asset, detection type,
//     cluster size, model confidence, priority, assignee and age — with an
//     explanation of how computer vision → clustering → dispatch works.
//   • Summary chips (critical, in progress, unassigned, avg confidence) and
//     quick priority filter.
//   • Reliability: isMounted guard, error state + retry, manual refresh,
//     websocket refetches debounced (bursts of events = one request).
//   • Dark mode: hover / header colours hoisted (were light-only literals).
//   • FIX: numeric ids crashed `.substring`; string lat/lng crashed `.toFixed`;
//     `status.replace('_')` only replaced the first underscore and crashed on null.
//   • Optional props: onSelectWorkOrder(wo), onLocate(wo), hideWhenEmpty (default true).
// --- END UPDATE
import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from 'react'; // --- UPDATE: + useRef/useMemo
import {
    Box, Flex, HStack, Text, Badge, Table, Thead, Tbody, Tr, Th, Td,
    useColorModeValue, Icon, Spinner, Tooltip,
    // --- UPDATE
    Button, IconButton, Progress, Wrap, WrapItem,
    // --- END UPDATE
} from '@chakra-ui/react';
import { MdOutlineAutoAwesome, MdCheckCircle, MdEngineering, MdWarning,
    // --- UPDATE
    MdRefresh, MdErrorOutline, MdMyLocation, MdOpenInNew, MdPersonOutline, MdHub, MdCameraAlt,
    // --- END UPDATE
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import { getAuthToken } from '../../utils/auth';
import { config } from '../../config';
import webSocketService from '../../services/websocketService';
// --- UPDATE
import { FieldHint } from './AiRoadHelper';
// --- END UPDATE

const MotionTr = motion(Tr);
const MotionBox = motion(Box);

// --- UPDATE: helpers
const PRIORITY_SCHEME = { critical: 'red', urgent: 'red', high: 'orange', medium: 'yellow', normal: 'yellow', low: 'gray' };
const PRIORITY_RANK = { critical: 0, urgent: 0, high: 1, medium: 2, normal: 2, low: 3 };
const WS_DEBOUNCE_MS = 400;
const pick = (obj, keys) => {
    for (const k of keys) {
        const v = k.split('.').reduce((o, p) => (o == null ? undefined : o[p]), obj);
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return undefined;
};
const toFixedSafe = (v, d = 4) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v).toFixed(d) : '—');
const shortId = (id) => String(id ?? '').replace(/-/g, '').substring(0, 8).toUpperCase() || '—';
const relativeAge = (ts) => {
    const t = new Date(ts).getTime();
    if (!Number.isFinite(t)) return '—';
    const m = Math.max(0, Math.round((Date.now() - t) / 60000));
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 48) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
};
const toPct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.round(n <= 1 ? n * 100 : n);
};
/** Normalise the fields the dispatch pipeline may put in different places. */
const describeWo = (wo) => {
    const meta = wo.metadata || wo.ai_metadata || {};
    const lat = pick(wo, ['target_lat', 'latitude', 'lat']) ?? pick(meta, ['lat', 'latitude', 'centroid.lat']);
    const lng = pick(wo, ['target_lng', 'longitude', 'lng']) ?? pick(meta, ['lng', 'longitude', 'centroid.lng']);
    return {
        road: pick(wo, ['road_name', 'asset_name', 'asset.name', 'road.name']) || pick(meta, ['road_name', 'nearest_road']),
        roadId: pick(wo, ['road_id', 'asset_id']) || pick(meta, ['road_id']),
        detection: pick(meta, ['anomaly_type', 'defect_type', 'detection_type', 'class']) || pick(wo, ['anomaly_type', 'category']),
        clusterSize: pick(meta, ['cluster_size', 'anomaly_count', 'detections', 'points']) ?? pick(wo, ['cluster_size', 'anomaly_count']),
        confidence: toPct(pick(meta, ['ai_confidence', 'confidence', 'avg_confidence']) ?? pick(wo, ['ai_confidence', 'confidence'])),
        severity: pick(meta, ['severity', 'avg_severity']),
        assignee: pick(wo, ['assigned_to_name', 'assignee_name', 'assigned_to.name', 'technician_name', 'assigned_to']),
        lat, lng,
        priority: String(wo.priority || 'medium').toLowerCase(),
        status: String(wo.status || 'open').toLowerCase(),
    };
};
// --- END UPDATE

const AIClusteredWorkOrdersTable = ({
    // --- UPDATE: optional
    onSelectWorkOrder, onLocate, hideWhenEmpty = true,
    // --- END UPDATE
} = {}) => {
    const [workOrders, setWorkOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // --- UPDATE
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [lastUpdated, setLastUpdated] = useState(null);
    const isMounted = useRef(true);
    const debounceRef = useRef(null);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; clearTimeout(debounceRef.current); };
    }, []);
    // --- END UPDATE

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('purple.200', 'purple.700');
    const headerBg = useColorModeValue('purple.50', 'purple.900');
    const tableHeadBg = useColorModeValue('gray.50', 'gray.750');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    // --- UPDATE: dark-mode safe literals
    const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
    const titleColor = useColorModeValue('purple.800', 'purple.100');
    const subtitleColor = useColorModeValue('purple.600', 'purple.200');
    const chipBg = useColorModeValue('white', 'whiteAlpha.100');
    // --- END UPDATE

    const API_BASE = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:3001/api/v1';

    const fetchAIWorkOrders = useCallback(async () => {
        try {
            const token = getAuthToken();
            // Fetch work orders specifically created by the AI user that are NOT completed/closed
            const res = await fetch(`${API_BASE}/work-orders?created_by_id=system_ai_user&status=open,pending,in_progress`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            // --- UPDATE
            if (!res.ok) throw new Error(`Server responded ${res.status}`);
            // --- END UPDATE
            const json = await res.json();
            // --- UPDATE
            if (!isMounted.current) return;
            // --- END UPDATE
            if (json.success) {
                setWorkOrders(json.data || []);
                // --- UPDATE
                setError(null);
                setLastUpdated(new Date());
                // --- END UPDATE
            }
        } catch (error) {
            console.error("Failed to fetch AI Work Orders:", error);
            // --- UPDATE
            if (isMounted.current) setError(error);
            // --- END UPDATE
        } finally {
            // --- UPDATE
            if (isMounted.current) { setIsLoading(false); setIsRefreshing(false); }
            // --- END UPDATE
        }
    }, [API_BASE]);

    // --- UPDATE: coalesce websocket bursts into one request
    const scheduleRefetch = useCallback(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { if (isMounted.current) fetchAIWorkOrders(); }, WS_DEBOUNCE_MS);
    }, [fetchAIWorkOrders]);

    const manualRefresh = useCallback(() => {
        setIsRefreshing(true);
        fetchAIWorkOrders();
    }, [fetchAIWorkOrders]);
    // --- END UPDATE

    useEffect(() => {
        fetchAIWorkOrders();

        // Listen for live completions from the mobile app to remove them from the list instantly
        const handleWoUpdate = (data) => {
            // --- UPDATE: null-safe payload
            if (!data) return;
            // --- END UPDATE
            if (data.status === 'completed' || data.status === 'closed') {
                setWorkOrders(prev => prev.filter(wo => String(wo.id) !== String(data.id)));
            } else {
                scheduleRefetch(); // Refresh if it's a new AI WO or status change
            }
        };

        webSocketService.subscribe('work_order_updated', handleWoUpdate);
        webSocketService.subscribe('work_order_completed', handleWoUpdate);
        webSocketService.subscribe('work_order_created', scheduleRefetch);

        return () => {
            webSocketService.unsubscribe('work_order_updated', handleWoUpdate);
            webSocketService.unsubscribe('work_order_completed', handleWoUpdate);
            webSocketService.unsubscribe('work_order_created', scheduleRefetch);
        };
    }, [fetchAIWorkOrders, scheduleRefetch]);

    // --- UPDATE: view-models, stats and filter
    const rows = useMemo(() => workOrders
        .map(wo => ({ wo, d: describeWo(wo) }))
        .sort((a, b) => (PRIORITY_RANK[a.d.priority] ?? 9) - (PRIORITY_RANK[b.d.priority] ?? 9)
            || new Date(b.wo.created_at) - new Date(a.wo.created_at)), [workOrders]);

    const stats = useMemo(() => {
        const conf = rows.map(r => r.d.confidence).filter(v => v != null);
        return {
            critical: rows.filter(r => PRIORITY_RANK[r.d.priority] === 0).length,
            high: rows.filter(r => PRIORITY_RANK[r.d.priority] === 1).length,
            inProgress: rows.filter(r => r.d.status === 'in_progress').length,
            unassigned: rows.filter(r => !r.d.assignee).length,
            avgConfidence: conf.length ? Math.round(conf.reduce((s, v) => s + v, 0) / conf.length) : null,
            detections: rows.reduce((s, r) => s + (Number(r.d.clusterSize) || 0), 0),
        };
    }, [rows]);

    const visibleRows = useMemo(() => (priorityFilter === 'all' ? rows
        : rows.filter(r => (priorityFilter === 'critical' ? PRIORITY_RANK[r.d.priority] === 0 : PRIORITY_RANK[r.d.priority] === 1))), [rows, priorityFilter]);
    // --- END UPDATE

    // --- UPDATE: keep the card visible when the request failed (was hidden → errors were invisible)
    if (!isLoading && workOrders.length === 0 && !error && hideWhenEmpty) return null; // Hide entirely if no active AI work orders
    // --- END UPDATE

    return (
        <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            mt={6}
        >
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} overflow="hidden" boxShadow="sm">
                {/* Header */}
                <Flex bg={headerBg} p={4} justify="space-between" align="center" borderBottomWidth="1px" borderColor={borderColor}
                    /* --- UPDATE */ wrap="wrap" gap={3} /* --- END UPDATE */>
                    <HStack spacing={3}>
                        <Box bg="purple.500" p={2.5} borderRadius="xl" boxShadow="0 4px 14px var(--chakra-colors-purple-200)">
                            <Icon as={MdOutlineAutoAwesome} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            {/* --- UPDATE: hoisted colours + explanation */}
                            <HStack spacing={0}>
                                <Text fontSize="md" fontWeight="800" color={titleColor}>Active AI-Dispatched Clusters</Text>
                                <FieldHint metric="ai_dispatch" />
                            </HStack>
                            <Text fontSize="xs" color={subtitleColor} fontWeight="500">
                                Work orders auto-generated by the Computer Vision pipeline
                            </Text>
                            {/* --- END UPDATE */}
                        </Box>
                    </HStack>
                    {/* --- UPDATE: refresh + count */}
                    <HStack spacing={2}>
                        {lastUpdated && <Text fontSize="2xs" color={subtitleColor}>Updated {relativeAge(lastUpdated)}</Text>}
                        <Tooltip label="Refresh" hasArrow>
                            <IconButton size="sm" variant="ghost" colorScheme="purple" aria-label="Refresh AI work orders"
                                icon={<Icon as={MdRefresh} />} onClick={manualRefresh} isLoading={isRefreshing} />
                        </Tooltip>
                    <Badge colorScheme="purple" variant="solid" borderRadius="full" px={3} py={1}>
                        {workOrders.length} DISPATCHED
                    </Badge>
                    </HStack>
                    {/* --- END UPDATE */}
                </Flex>

                {/* --- UPDATE: pipeline summary + filter */}
                {rows.length > 0 && (
                    <Flex px={4} py={3} gap={2} wrap="wrap" align="center" borderBottomWidth="1px" borderColor={borderColor}>
                        <Wrap spacing={2} flex="1">
                            {[
                                { key: 'all', label: `All ${rows.length}`, scheme: 'purple' },
                                { key: 'critical', label: `Critical ${stats.critical}`, scheme: 'red' },
                                { key: 'high', label: `High ${stats.high}`, scheme: 'orange' },
                            ].map(f => (
                                <WrapItem key={f.key}>
                                    <Button size="xs" borderRadius="full" colorScheme={f.scheme}
                                        variant={priorityFilter === f.key ? 'solid' : 'outline'}
                                        onClick={() => setPriorityFilter(f.key)} aria-pressed={priorityFilter === f.key}>
                                        {f.label}
                                    </Button>
                                </WrapItem>
                            ))}
                        </Wrap>
                        <HStack spacing={2} fontSize="2xs">
                            <Badge bg={chipBg} variant="outline" colorScheme="blue">{stats.inProgress} in progress</Badge>
                            <Badge bg={chipBg} variant="outline" colorScheme={stats.unassigned ? 'orange' : 'green'}>{stats.unassigned} unassigned</Badge>
                            {stats.detections > 0 && <Badge bg={chipBg} variant="outline" colorScheme="purple">{stats.detections} detections</Badge>}
                            {stats.avgConfidence != null && (
                                <Tooltip label="Average computer-vision confidence across these clusters" hasArrow>
                                    <Badge bg={chipBg} variant="outline" colorScheme="purple" cursor="help">avg conf. {stats.avgConfidence}%</Badge>
                                </Tooltip>
                            )}
                        </HStack>
                    </Flex>
                )}
                {error && (
                    <HStack px={4} py={2} spacing={2} color="red.400" fontSize="xs" borderBottomWidth="1px" borderColor={borderColor}>
                        <Icon as={MdErrorOutline} />
                        <Text>Couldn't load AI work orders: {error.message}</Text>
                        <Button size="xs" variant="link" colorScheme="blue" onClick={manualRefresh}>Retry</Button>
                    </HStack>
                )}
                {/* --- END UPDATE */}

                {/* Table */}
                <Box overflowX="auto" maxH="400px">
                    <Table size="sm" variant="simple">
                        <Thead bg={tableHeadBg} position="sticky" top={0} zIndex={1}>
                            <Tr>
                                <Th py={3}>WO ID</Th>
                                <Th>TITLE & TARGET</Th>
                                {/* --- UPDATE */}
                                <Th>AI DETECTION</Th>
                                {/* --- END UPDATE */}
                                <Th>PRIORITY</Th>
                                <Th>STATUS</Th>
                                {/* --- UPDATE */}
                                <Th>ASSIGNED</Th>
                                {/* --- END UPDATE */}
                                <Th>CREATED</Th>
                                {/* --- UPDATE */}
                                {(onSelectWorkOrder || onLocate) && <Th textAlign="right">ACTIONS</Th>}
                                {/* --- END UPDATE */}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {isLoading ? (
                                <Tr><Td colSpan={8} textAlign="center" py={8}><Spinner color="purple.500" /></Td></Tr>
                            ) : (
                                <AnimatePresence>
                                    {/* --- UPDATE: sorted + filtered view-models */}
                                    {visibleRows.map(({ wo, d }) => (
                                    // --- END UPDATE
                                        <MotionTr
                                            key={wo.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, scale: 0.9, backgroundColor: '#E6FFFA' }} // Flashes green before removing
                                            transition={{ duration: 0.3 }}
                                            _hover={{ bg: rowHover }}
                                        >
                                            <Td fontWeight="bold" fontFamily="mono" color="purple.500" fontSize="xs">
                                                {/* --- UPDATE: numeric-id safe */}
                                                {shortId(wo.id)}
                                                {/* --- END UPDATE */}
                                            </Td>
                                            <Td>
                                                <Text fontWeight="800" fontSize="xs">{wo.title}</Text>
                                                {/* --- UPDATE: road + description */}
                                                {d.road && <Text fontSize="2xs" fontWeight="700" color="blue.400" noOfLines={1}>{d.road}</Text>}
                                                {wo.description && <Text fontSize="2xs" color={mutedColor} noOfLines={1} maxW="300px">{wo.description}</Text>}
                                                {/* --- END UPDATE */}
                                                <Text fontSize="2xs" color={mutedColor} noOfLines={1} maxW="300px">
                                                    {/* --- UPDATE: string-safe coordinates */}
                                                    Lat: {toFixedSafe(d.lat)}, Lng: {toFixedSafe(d.lng)}
                                                    {/* --- END UPDATE */}
                                                </Text>
                                            </Td>
                                            {/* --- UPDATE: AI detection cell */}
                                            <Td>
                                                <HStack spacing={1} mb={d.confidence != null ? 1 : 0}>
                                                    <Icon as={MdCameraAlt} boxSize={3} color="purple.400" />
                                                    <Text fontSize="xs" fontWeight="700" textTransform="capitalize">
                                                        {d.detection ? String(d.detection).replace(/_/g, ' ') : 'Road defect'}
                                                    </Text>
                                                    {d.clusterSize != null && (
                                                        <Tooltip label={`${d.clusterSize} dashcam detections grouped into this job`} hasArrow>
                                                            <Badge fontSize="9px" colorScheme="purple" variant="subtle" display="flex" alignItems="center" gap={0.5} cursor="help">
                                                                <Icon as={MdHub} boxSize={2.5} />{d.clusterSize}
                                                            </Badge>
                                                        </Tooltip>
                                                    )}
                                                </HStack>
                                                {d.confidence != null && (
                                                    <Tooltip label={`Computer-vision confidence ${d.confidence}%${d.confidence < 60 ? ' — verify on site before dispatching a crew' : ''}`} hasArrow>
                                                        <HStack spacing={1} w="90px" cursor="help">
                                                            <Progress value={d.confidence} size="xs" flex={1} borderRadius="full"
                                                                colorScheme={d.confidence >= 80 ? 'green' : d.confidence >= 60 ? 'yellow' : 'orange'} />
                                                            <Text fontSize="2xs" fontFamily="mono" color={mutedColor}>{d.confidence}%</Text>
                                                        </HStack>
                                                    </Tooltip>
                                                )}
                                            </Td>
                                            {/* --- END UPDATE */}
                                            <Td>
                                                <Badge colorScheme={/* --- UPDATE */ PRIORITY_SCHEME[d.priority] || 'orange' /* --- END UPDATE */} fontSize="2xs" borderRadius="full">
                                                    {wo.priority}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <HStack spacing={1}>
                                                    <Icon as={wo.status === 'in_progress' ? MdEngineering : MdWarning}
                                                          color={wo.status === 'in_progress' ? 'blue.500' : 'orange.500'} boxSize={3} />
                                                    <Text fontSize="xs" fontWeight="600" textTransform="capitalize">
                                                        {/* --- UPDATE: all underscores, null-safe */}
                                                        {d.status.replace(/_/g, ' ')}
                                                        {/* --- END UPDATE */}
                                                    </Text>
                                                </HStack>
                                            </Td>
                                            {/* --- UPDATE */}
                                            <Td>
                                                {d.assignee ? (
                                                    <HStack spacing={1}>
                                                        <Icon as={MdPersonOutline} boxSize={3} color={mutedColor} />
                                                        <Text fontSize="xs" noOfLines={1}>{typeof d.assignee === 'object' ? (d.assignee.name || d.assignee.id) : d.assignee}</Text>
                                                    </HStack>
                                                ) : <Badge colorScheme="orange" variant="subtle" fontSize="2xs">Unassigned</Badge>}
                                            </Td>
                                            {/* --- END UPDATE */}
                                            <Td fontSize="xs" color={mutedColor}>
                                                {/* --- UPDATE: relative age, full time on hover */}
                                                <Tooltip label={new Date(wo.created_at).toLocaleString()} hasArrow>
                                                    <Text as="span" cursor="help">{relativeAge(wo.created_at)}</Text>
                                                </Tooltip>
                                                {/* --- END UPDATE */}
                                            </Td>
                                            {/* --- UPDATE */}
                                            {(onSelectWorkOrder || onLocate) && (
                                                <Td textAlign="right">
                                                    <HStack spacing={0} justify="flex-end">
                                                        {onLocate && (
                                                            <Tooltip label="Show on map" hasArrow>
                                                                <IconButton size="xs" variant="ghost" aria-label={`Show ${wo.title} on map`} icon={<Icon as={MdMyLocation} />} onClick={() => onLocate(wo)} />
                                                            </Tooltip>
                                                        )}
                                                        {onSelectWorkOrder && (
                                                            <Tooltip label="Open work order" hasArrow>
                                                                <IconButton size="xs" variant="ghost" aria-label={`Open ${wo.title}`} icon={<Icon as={MdOpenInNew} />} onClick={() => onSelectWorkOrder(wo)} />
                                                            </Tooltip>
                                                        )}
                                                    </HStack>
                                                </Td>
                                            )}
                                            {/* --- END UPDATE */}
                                        </MotionTr>
                                    ))}
                                </AnimatePresence>
                            )}
                            {/* --- UPDATE: filtered-empty / empty states */}
                            {!isLoading && visibleRows.length === 0 && (
                                <Tr>
                                    <Td colSpan={8} textAlign="center" py={6}>
                                        <HStack justify="center" spacing={2} color={mutedColor}>
                                            <Icon as={MdCheckCircle} color="green.400" />
                                            <Text fontSize="xs">{rows.length ? 'No work orders at this priority.' : 'No active AI-dispatched work orders.'}</Text>
                                        </HStack>
                                    </Td>
                                </Tr>
                            )}
                            {/* --- END UPDATE */}
                        </Tbody>
                    </Table>
                </Box>
            </Box>
        </MotionBox>
    );
};

export default memo(AIClusteredWorkOrdersTable);